import { useCallback, useState, useEffect } from 'react';
import { Editor, Range, Transforms, BaseRange, Path, Point } from 'slate';

/**
 * State interface for the slash command system.
 * This manages the complete lifecycle of a slash command from trigger to execution:
 *
 * - isActive: True when a slash command is being typed (triggered by '/')
 * - commandText: The text after the '/' (e.g., in '/gif', this would be 'gif')
 * - triggerRange: The Slate Range where the command started, used for:
 *   - Positioning the command menu
 *   - Determining if selection moves out of command
 *   - Deleting the command text when a command is executed
 */
interface SlashState {
  isActive: boolean;
  commandText: string;
  triggerRange: Range | null;
}

/**
 * Extends Slate's BaseRange to support slash command decorations.
 * Used by Slate's decoration system to:
 * - Apply special styling to command text
 * - Make command text non-editable
 * - Visually distinguish commands from regular text
 *
 * When isSlashCommand is true, the text will be rendered with command styling
 * (typically a pill-like background with monospace font).
 */
interface DecoratedRange extends BaseRange {
  isSlashCommand?: boolean;
}

/**
 * The complete interface for slash command functionality.
 * Provides everything needed to:
 *
 * - Track command state (isOpen, commandText)
 * - Position command UI (targetRange)
 * - Handle keyboard input (onKeyDown)
 * - Clean up commands (closeMenu)
 * - Style command text (decorate)
 *
 * This is typically used by a parent component that renders
 * the command menu and manages command execution.
 */
interface UseSlashCommandsReturn {
  isOpen: boolean;
  commandText: string;
  targetRange: Range | null;
  onKeyDown: (event: React.KeyboardEvent) => void;
  closeMenu: () => void;
  decorate: (entry: [any, number[]]) => DecoratedRange[];
}

/**
 * Validates if a Slate Point is still valid in the editor.
 * This is crucial because Points can become invalid when:
 * - Text is deleted
 * - The document structure changes
 * - Nodes are moved or removed
 *
 * Invalid points can cause errors in transforms and selections,
 * so we need to validate before using them.
 *
 * @param editor - The Slate editor instance
 * @param point - The Point to validate, which may be null/undefined
 * @returns Type predicate narrowing to valid Point
 */
function isValidPoint(editor: Editor, point: Point | null | undefined): point is Point {
  return point != null && Editor.hasPath(editor, point.path);
}

/**
 * Validates if a Slate Range is still valid in the editor.
 * A Range is valid only if both its anchor and focus points are valid.
 *
 * This is used to ensure safety when:
 * - Applying transforms
 * - Setting selections
 * - Calculating command positions
 * - Deleting command text
 *
 * @param editor - The Slate editor instance
 * @param range - The Range to validate, which may be null/undefined
 * @returns Type predicate narrowing to valid Range
 */
function _isValidRange(editor: Editor, range: Range | null | undefined): range is Range {
  return range != null && isValidPoint(editor, range.anchor) && isValidPoint(editor, range.focus);
}

/**
 * Safely gets a Point from the editor at a specific location.
 * Wraps Slate's Editor.point with additional safety:
 * - Handles undefined returns
 * - Catches potential errors
 * - Validates the returned point
 *
 * This is used when we need to:
 * - Calculate command positions
 * - Find insertion points
 * - Determine selection boundaries
 *
 * @param editor - The Slate editor instance
 * @param at - Location to get point from (can be Path, Point, or Range)
 * @returns A valid Point or undefined if the location is invalid
 */
function getEditorPoint(editor: Editor, at: Parameters<typeof Editor.point>[1]): Point | undefined {
  try {
    const point = Editor.point(editor, at);
    return isValidPoint(editor, point) ? point : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Hook that implements a slash command system in a Slate editor.
 *
 * Key Features:
 * 1. Command Triggering:
 *    - Activates when user types '/'
 *    - Handles escaped slashes (\\/) correctly
 *    - Tracks command position and text
 *
 * 2. Command Text Management:
 *    - Accumulates text after the slash
 *    - Handles backspace and deletion
 *    - Maintains proper editor state
 *
 * 3. Selection Handling:
 *    - Deactivates when selection moves out of command
 *    - Maintains proper command ranges
 *    - Handles edge cases (e.g., selecting across commands)
 *
 * 4. Decoration System:
 *    - Applies visual styling to command text (e.g. a pill-like background)
 *    - Makes commands non-editable
 *    - Handles updates efficiently
 *
 *
 * @param editor - The Slate editor instance to add slash commands to
 * @returns Interface for managing slash commands
 */
export function useSlashCommands(editor: Editor): UseSlashCommandsReturn {
  // Track complete slash command state
  const [state, setState] = useState<SlashState>({
    isActive: false,
    commandText: '',
    triggerRange: null
  });

  const closeMenu = useCallback(() => {
    setState({
      isActive: false,
      commandText: '',
      triggerRange: null
    });
  }, []);

  // Check if selection is within command range using Slate's APIs
  const checkSelection = useCallback(() => {
    if (state.isActive && state.triggerRange && editor.selection) {
      const commandEnd = Editor.end(editor, state.triggerRange.anchor.path);
      const commandRange = {
        anchor: state.triggerRange.anchor,
        focus: {
          path: commandEnd.path,
          offset: state.triggerRange.anchor.offset + state.commandText.length + 1
        }
      };

      const isInRange = Range.includes(commandRange, editor.selection.anchor);

      if (!isInRange) {
        closeMenu();
      }
    }
  }, [editor, state.isActive, state.triggerRange, state.commandText, closeMenu]);

  // Watch for selection changes using Slate's selection state
  useEffect(() => {
    checkSelection();
  }, [editor.selection, checkSelection]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const { selection } = editor;

      // Handle escape to close
      if (state.isActive && event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
        return;
      }

      // Handle backspace
      if (state.isActive && event.key === 'Backspace') {
        event.preventDefault();

        if (state.commandText === '') {
          closeMenu();
          return;
        }

        if (state.triggerRange) {
          const point = getEditorPoint(editor, {
            path: state.triggerRange.anchor.path,
            offset: state.triggerRange.anchor.offset + state.commandText.length + 1
          });

          if (!point) {
            closeMenu();
            return;
          }

          Transforms.delete(editor, { at: point, unit: 'character', reverse: true });
        }

        setState((prev) => ({
          ...prev,
          commandText: prev.commandText.slice(0, -1)
        }));
        return;
      }

      // Detect slash to open (but not escaped slash)
      if (event.key === '/' && selection && !state.isActive) {
        event.preventDefault();

        const prevPoint = Editor.before(editor, selection.anchor);
        const prevChar = isValidPoint(editor, prevPoint)
          ? Editor.string(editor, { anchor: prevPoint, focus: selection.anchor })
          : '';

        if (prevChar === '\\') {
          if (isValidPoint(editor, prevPoint)) {
            Transforms.delete(editor, { at: prevPoint, unit: 'character' });
            Transforms.insertText(editor, '/');
          }
          return;
        }

        const point = getEditorPoint(editor, selection);
        if (!point) {
          return;
        }

        Transforms.insertText(editor, '/', { at: point });
        setState({
          isActive: true,
          commandText: '',
          triggerRange: { anchor: point, focus: point }
        });
        return;
      }

      // Add to command text if menu is open
      if (
        state.isActive &&
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        event.preventDefault();

        if (state.triggerRange) {
          const point = getEditorPoint(editor, {
            path: state.triggerRange.anchor.path,
            offset: state.triggerRange.anchor.offset + state.commandText.length + 1
          });

          if (!point) {
            closeMenu();
            return;
          }

          Transforms.insertText(editor, event.key, { at: point });
        }

        setState((prev) => ({
          ...prev,
          commandText: prev.commandText + event.key
        }));
        return;
      }
    },
    [editor, state, closeMenu]
  );

  // Create decorator for visual styling
  const decorate = useCallback(
    ([_node, path]: [any, number[]]) => {
      const ranges: DecoratedRange[] = [];

      if (state.isActive && state.triggerRange) {
        const triggerPath = Editor.path(editor, state.triggerRange);

        if (Path.equals(path, triggerPath)) {
          const start = Range.start(state.triggerRange);
          if (!isValidPoint(editor, start)) {
            return ranges;
          }

          const end = getEditorPoint(editor, {
            path: start.path,
            offset: start.offset + state.commandText.length + 1
          });

          if (!end) {
            return ranges;
          }

          ranges.push({
            anchor: start,
            focus: end,
            isSlashCommand: true
          });
        }
      }

      return ranges;
    },
    [state.isActive, state.triggerRange, state.commandText, editor]
  );

  return {
    isOpen: state.isActive,
    commandText: state.commandText,
    targetRange: state.triggerRange,
    onKeyDown,
    closeMenu,
    decorate
  };
}
